import loadable from "@loadable/component";
import React, { Fragment, useEffect, useCallback } from "react";
import { observer } from "mobx-react-lite";

import HeaderSearchBar from "@components/Main/Shared/HeaderSearchBar";
import Footer from "@components/Main/Shared/Footer";
import ReviewItem from "@components/Global/Custom/ReviewItem";
import RecommendedTiles from "@components/Dashboard/Client/Shared/RecommendedTiles.jsx";
import ReviewsNav from "./ReviewsNav.jsx";
import LoadingButton from "@components/Global/Custom/Button";

import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";

import NavigateNextIcon from "@material-ui/icons/NavigateNext";

import useStores from "@hooks/useStores.js";
import useStyles from "@hooks/useStyles.js";
import ImageDialog from "@components/Global/Custom/ImageDialog/ImageDialog.jsx";
import EmptyMessage from "@custom/EmptyMessage";

import Background from "@components/Dashboard/Client/Shared/Background";

import { adjective as proAdjective } from "@language/provider";
import PendingReviewItem from "./PendingReviewItem.jsx";

const { ENV } = process.env;

const Reviews = observer(() => {
	const { clientReviewsStore: store } = useStores();
	const classes = useStyles("ClientReviews");

	const handleLoadMore = async () => {
		await store.loadMore();
	};

	const init = useCallback(async () => {
		await store.init();
	}, [store.currentSort]);

	useEffect(() => {
		init();
		return () => {};
	}, [init]);

	const showPending = true;

	return (
		<Fragment>
			{/* <HeaderSearchBar /> */}
			<Background>
				<main className={classes.background}>
					<Container maxWidth={false} style={{ maxWidth: 750 }}>
						<Box
							display={"flex"}
							flexDirection={"column"}
							width={"100%"}
							mb={6}
						>
							<Breadcrumbs
								separator={<NavigateNextIcon fontSize="small" />}
								aria-label="breadcrumb"
								className={classes.bread}
							>
								<a href="/client/account">Account</a>
								<div>Reviews</div>
							</Breadcrumbs>
						</Box>

						{(ENV === "stage" || ENV === "development") && (
							<>
								{showPending && (
									<div>
										<Box display={"flex"} flexDirection={"column"}>
											<Box display={"flex"} alignItems={"center"}>
												<Box
													fontWeight={600}
													fontSize={{ xs: 28, sm: 32 }}
													mr={4}
												>
													Pending reviews
												</Box>
											</Box>
											<Box mt={1}>
												{`Awaiting your feedback: Review these ${proAdjective.plural} soon!`}
											</Box>
										</Box>
										<Box component="section" width="100%" mb={16} mt={8}>
											<PendingReviewItem />
										</Box>
									</div>
								)}
							</>
						)}

						<div>
							<Box
								display={"flex"}
								justifyContent={"space-between"}
								alignItems={"flex-end"}
								width={"100%"}
								mb={8}
							>
								<Box display={"flex"} flexDirection={"column"}>
									<Box display={"flex"} alignItems={"center"}>
										<Box fontWeight={600} fontSize={{ xs: 28, sm: 32 }} mr={4}>
											My reviews
										</Box>
									</Box>
									<Box mt={1}>Browse the reviews you've shared. </Box>
								</Box>
								{!!store.reviews.length && <ReviewsNav />}
							</Box>
							<Box component="section" width="100%">
								{store.reviews.map((review) => (
									<Box mb={3}>
										<Card>
											<CardContent>
												<ReviewItem
													key={review._id}
													review={review}
													displayAuthorName={false}
													showReplyButton={false}
												/>
											</CardContent>
										</Card>
									</Box>
								))}

								{store.hasMore && store.reviews.length > 0 && (
									<Box mt={12} display={"flex"} justifyContent={"center"}>
										<LoadingButton
											disableElevation
											size={"large"}
											variant={"outlined"}
											color={"default"}
											onClick={handleLoadMore}
											label={"See more"}
											style={{ backgroundColor: "#FFF" }}
										/>
									</Box>
								)}

								{!store.reviews.length && (
									<Box
										width={"100%"}
										height={300}
										display={"flex"}
										justifyContent={"center"}
										alignItems={"center"}
									>
										<EmptyMessage
											header={"Your reviews show up here."}
											body={"You haven't left any reviews yet"}
											variant="reviews"
										/>
									</Box>
								)}
							</Box>
						</div>
					</Container>
				</main>
				<Footer thin />
			</Background>
			{/* <RecommendedTiles /> */}
			<ImageDialog />
		</Fragment>
	);
});

export default Reviews;

export const loader = (route) =>
	loadable(() => import("./Reviews.component.js"), { ssr: !!route.ssr });
